// Hosts-Health-Store für die Dashboard-Karte. Aggregiert aus dem gemeinsamen
// Stand der Rechner (rechner.svelte.ts):
//   cluster-stats-Store        → online-Status, sleep_ok und Updates je Rechner
//   host.lage (Protokoll)      → Anomalien gegen die Baseline, Rechner, die
//                                offline sein dürfen (DB-Overrides
//                                "expected_offline_ok"), Rechner für den Verlauf
//   /v2-api/host_stats/timeseries → der Verlauf (Spark-Line) dieses Rechners
//
// Kein neuer Backend-Endpoint nötig -- die Aggregation läuft hier. Seit
// 03.11.2026 holt dieser Store weder baselines noch overrides noch einen
// Cluster-Snapshot selbst: die Baselines rechnet das Backend (5-min-Job, 10-min-
// Glättung) und schickt Anomalien und Ausnahmen mit host.lage (docs/protokoll.md
// › „WAN und Rechner über das Protokoll“); nach einem Override kommt die Lage
// binnen Sekunden neu.
//
// Neu gerechnet wird (rein lokal, ohne Abruf): bei jeder Meldung des
// cluster-stats-Stores (nur bei geänderter Signatur: online, sleep_ok, Updates),
// bei geänderter Lage und alle 30 s -- die Grace-Phase unten läuft mit der Zeit
// ab, ohne dass sich ein Eingang ändert. Abgerufen wird nur der Verlauf: beim
// Start, sobald sich (Rechner, Metrik) des Verlaufs ändern (die schärfste
// Anomalie, sonst "cpu" des Verlauf-Rechners aus der Lage), als Revalidator
// und alle 5 min.
//
// SWR: ja. localStorage-Snapshot beim Boot, frische Daten im Hintergrund; die
// Ablage wird nur bei geändertem Ergebnis geschrieben.

import { untrack } from "svelte";
import { SvelteMap } from "svelte/reactivity";
import { getJSON } from "./api";
import { registerRevalidator } from "./revalidate.svelte";

import { loadPersisted, persist } from "./persistent-summary.svelte";
import {
  getClusterStats,
  startClusterStats,
  subscribeClusterStats,
} from "./cluster-stats.svelte";
import { isEntityDisabled } from "./entity-disables.svelte";
import { getHostLage } from "./rechner.svelte";
import { anomalienAusProtokoll, offlineOkAusProtokoll, verlaufHostAusProtokoll } from "./rechner-protokoll";
import { gleich } from "./storage-protokoll";

export interface HostsSummary {
  status: "ok" | "warning" | "problem";
  hosts_total: number;
  hosts_online: number;
  hosts_offline: number;
  hosts_with_anomaly: number;
  // Apt-Updates aggregiert ueber alle Hosts. Quelle: cluster-stats
  // (h.updates aus homelab/updates/<host> MQTT).
  updates_pending_hosts: number;
  updates_pending_total: number;
  updates_security: number;
  // Bis zu 3 prominente Anomalien für Subtitle/Detail.
  top_anomalies: HostAnomaly[];
  // Trajektorie für die Spark-Line: Host + Metrik die "interessant"
  // genug sind, plus die Datapoints. Bei Anomalie der schärfste
  // (host, metric); bei ok ein Default-Host mit CPU.
  spark?: HostsSpark;
}

export interface HostsSpark {
  host_id: string;
  metric: string;
  values: (number | null)[];
}

export interface HostAnomaly {
  host_id: string;
  metric: string;
  current: number;
  avg: number;
  z_score: number;
}

const STORAGE_KEY = "hosts";

// Grace-Phase fuer die Offline-ZAEHLUNG (nicht fuer die Live-Anzeige):
// Ein Host gilt fuer den "X Hosts offline"-Alarm erst als ausgefallen,
// wenn er laenger als HOST_OFFLINE_GRACE_MS am Stueck stumm ist. Grund:
// die cluster.Monitor-Frische kippt schon nach 30 s MQTT-Stille auf
// offline (Agent-Neustart, kurzer Netz-Blip, Backup-Fenster saettigt die
// Anbindung) -- ohne Daempfung feuerte der alerts-store daraus SOFORT eine
// "Host offline"-Problem-Meldung, obwohl der Host Sekunden spaeter wieder
// da ist. Der Backend-Push ist mit 3 Strikes x 60 s ohnehin entprellt
// (alerts/checker), UND WAN/DNS konsumieren im Frontend ebenfalls einen
// entprellten Zustand -- Hosts ziehen hier nach. status_since kommt aus
// dem cluster.Monitor (Transition-Zeitpunkt, resettet bei Flap -> Flapping
// wird korrekt unterdrueckt). Der Wert deckt die Backend-Strike-Fenster
// (~2 Ticks) ab, damit Dashboard-Alarm und Push zusammen kommen.
const HOST_OFFLINE_GRACE_MS = 150_000;

const _summary = new SvelteMap<string, HostsSummary>();
let _started = false;
let _timer: ReturnType<typeof setInterval> | undefined;
let _verlaufTimer: ReturnType<typeof setInterval> | undefined;
let _unsubCluster: (() => void) | undefined;
let _unsubRevalidate: (() => void) | undefined;
let _beobachter: (() => void) | undefined;

// Rechnen -- die Grace-Phase läuft mit der Uhr ab.
const RECHNEN_MS = 30_000;
// Der Verlauf ändert sich im Minutentakt kaum sichtbar (24 h Fenster).
const VERLAUF_MS = 300_000;

// Der Verlauf, der zur Karte gehört: Rechner und Metrik.
interface VerlaufZiel {
  host: string;
  metric: string;
}

// Der zuletzt geholte Verlauf; `_verlaufVersuch` ist das Ziel des jüngsten
// Abrufs (auch eines gescheiterten: der nächste Versuch kommt mit dem
// Revalidator oder dem 5-min-Takt, nicht bei jeder Neurechnung).
let _spark: HostsSpark | undefined;
let _verlaufVersuch = "";
let _verlaufLauf = 0;

function schluessel(z: VerlaufZiel): string {
  return `${z.host}\u0000${z.metric}`;
}

// Bei Anomalie der schärfste Datenpunkt, sonst der Verlauf-Rechner der Lage
// (forge, falls er eine Baseline hat) mit der Standard-Metrik cpu.
function verlaufZiel(anomalies: HostAnomaly[], verlaufHost: string): VerlaufZiel | undefined {
  if (anomalies.length > 0) return { host: anomalies[0].host_id, metric: anomalies[0].metric };
  return verlaufHost ? { host: verlaufHost, metric: "cpu" } : undefined;
}

async function holeVerlauf(ziel: VerlaufZiel, erzwingen: boolean): Promise<void> {
  const k = schluessel(ziel);
  if (!erzwingen && k === _verlaufVersuch) return;
  _verlaufVersuch = k;
  const lauf = ++_verlaufLauf;
  try {
    const points =
      (await getJSON<{ value: number | null }[] | null>(
        `/v2-api/host_stats/timeseries?host=${encodeURIComponent(ziel.host)}&metric=${encodeURIComponent(ziel.metric)}&hours=24`,
      )) ?? [];
    // Eine Antwort, die erst nach einem neueren Abruf oder dem Stopp ankommt, gilt nicht mehr.
    if (lauf !== _verlaufLauf || !_started) return;
    _spark = { host_id: ziel.host, metric: ziel.metric, values: points.map((p) => p.value) };
  } catch {
    // Karte ohne Spark; der nächste Rechenlauf (spätestens der 30-s-Takt)
    // versucht dasselbe Ziel erneut.
    if (lauf === _verlaufLauf) _verlaufVersuch = "";
    return;
  }
  neuRechnen();
}

// Den Verlauf des jetzigen Ziels erneut holen (Revalidator, 5-min-Takt).
function erneuereVerlauf(): void {
  const lage = getHostLage();
  if (!lage) return;
  const ziel = verlaufZiel(anomalienAusProtokoll(lage), verlaufHostAusProtokoll(lage));
  if (ziel) void holeVerlauf(ziel, true);
}

// Rechnet die Karte aus dem Cluster-Snapshot und der Lage. Ohne einen von
// beiden (noch nichts da) bleibt die Ablage stehen.
function neuRechnen(): void {
  const clusters = getClusterStats();
  const lage = getHostLage();
  if (!clusters || !lage) return;

  // hosts_offline zählt nur Hosts deren Offline-Status nicht
  // erwartet ist. Zwei Quellen für "schläft normal offline":
  //   (a) DB-Override expected_offline_ok=true (G28.7, neu) -- kommt in der Lage
  //   (b) config.yml-Flag sleep_ok=true (legacy, schon im Code
  //       verankert via cluster.Monitor → ClusterStatsHost.sleep_ok)
  // Der DB-Override gewinnt; der config-Flag bleibt als Default
  // damit bestehende Hosts (MacBook mit sleep_ok: true) ohne UI-
  // Eingabe sofort korrekt gezählt werden.
  const expectedOffline = new Set(offlineOkAusProtokoll(lage));
  let total = 0;
  let online = 0;
  let offline = 0;
  let updatesHosts = 0;
  let updatesTotal = 0;
  let updatesSec = 0;
  const nowMs = Date.now();
  for (const c of clusters) {
    // Bewusst deaktivierte Cluster (z.B. SON-Insel bei Internet-Stoerung)
    // fallen komplett aus den Health-Zaehlern -- sie sind nicht aktiv
    // ueberwacht, also weder "online" noch "down". So zeigt das Dashboard
    // keinen falschen "X DOWN"-Alarm fuer absichtlich stillgelegte Hosts.
    if (isEntityDisabled("cluster", c.id)) continue;
    for (const h of c.hosts) {
      total++;
      if (h.online === true) online++;
      else if (h.online === false) {
        const sleeps = expectedOffline.has(h.id) || h.sleep_ok;
        // Grace: frisch verstummte Hosts noch NICHT als offline zaehlen
        // (kurzer Blip). status_since = Transition-Zeitpunkt; fehlt er,
        // behandeln wir den Host als eben-erst-offline (in Grace), statt
        // sofort zu alarmieren -- der Monitor ankert since immer, ein
        // fehlender Wert waere ein Erst-Boot-/Noise-Fall.
        const since = h.state.status_since
          ? Date.parse(h.state.status_since)
          : NaN;
        const settledOffline =
          !Number.isNaN(since) && nowMs - since >= HOST_OFFLINE_GRACE_MS;
        if (!sleeps && settledOffline) offline++;
      }
      if (h.updates && h.updates.pending > 0) {
        updatesHosts++;
        updatesTotal += h.updates.pending;
        updatesSec += h.updates.security_pending;
      }
    }
  }

  // Schärfste Anomalien zuerst (höchster |Z|) -- so sortiert sie das Backend.
  const anomalies = anomalienAusProtokoll(lage);
  const anomalyHosts = new Set(anomalies.map((a) => a.host_id));

  let status: HostsSummary["status"];
  if (offline > 0) status = "problem";
  else if (anomalyHosts.size > 0) status = "warning";
  else status = "ok";

  // Spark-Line-Quelle: bei Anomalie der schärfste Datenpunkt, sonst der
  // Verlauf-Rechner der Lage (Default-Metrik: cpu). Nur der Verlauf, der zum
  // jetzigen Ziel gehört, wird gezeigt.
  const ziel = verlaufZiel(anomalies, verlaufHostAusProtokoll(lage));
  if (ziel) void holeVerlauf(ziel, false);
  const spark = ziel && _spark && _spark.host_id === ziel.host && _spark.metric === ziel.metric ? _spark : undefined;

  const next: HostsSummary = {
    status,
    hosts_total: total,
    hosts_online: online,
    hosts_offline: offline,
    hosts_with_anomaly: anomalyHosts.size,
    updates_pending_hosts: updatesHosts,
    updates_pending_total: updatesTotal,
    updates_security: updatesSec,
    top_anomalies: anomalies.slice(0, 3),
    spark,
  };
  // Nur bei geändertem Ergebnis setzen und speichern.
  const alt = _summary.get("current");
  if (alt && gleich(alt, next)) return;
  _summary.set("current", next);
  persist(STORAGE_KEY, next);
}

export function startHostsSummary(): void {
  if (_started) return;
  _started = true;
  _unsubRevalidate = registerRevalidator(() => {
    neuRechnen();
    erneuereVerlauf();
  });
  // SWR: letzten Snapshot sofort einspielen, damit die Dashboard-Card
  // beim Reload nicht "lade..." zeigt.
  const cached = loadPersisted<HostsSummary>(STORAGE_KEY);
  if (cached) {
    _summary.set("current", cached);
    _spark = cached.spark;
  }
  // cluster-stats ist Pflicht-Quelle; idempotenter Start hier deckt den
  // Fall ab dass jemand hosts-summary ohne App.svelte-Boot startet.
  startClusterStats();
  _unsubCluster = subscribeClusterStats(() => neuRechnen());
  // Die Lage (Anomalien, erlaubte Ausfälle, Verlauf-Rechner) kommt mit dem
  // Protokoll; jede Änderung rechnet neu.
  _beobachter = $effect.root(() => {
    $effect(() => {
      void getHostLage();
      untrack(neuRechnen);
    });
  });
  _timer = setInterval(neuRechnen, RECHNEN_MS);
  _verlaufTimer = setInterval(erneuereVerlauf, VERLAUF_MS);
}

export function stopHostsSummary(): void {
  _started = false;
  if (_timer) {
    clearInterval(_timer);
    _timer = undefined;
  }
  if (_verlaufTimer) {
    clearInterval(_verlaufTimer);
    _verlaufTimer = undefined;
  }
  _beobachter?.();
  _beobachter = undefined;
  if (_unsubCluster) {
    _unsubCluster();
    _unsubCluster = undefined;
  }
  _unsubRevalidate?.();
  _unsubRevalidate = undefined;
  _verlaufLauf++;
  _verlaufVersuch = "";
  _spark = undefined;
  _summary.clear();
}

export function getHostsSummary(): HostsSummary | undefined {
  return _summary.get("current");
}
